import { Collection, Db } from 'mongodb';
import { Domain } from '../../../strategic-design/domain/entities/Domain';
import type { IDomainRepository } from '../../domain/ports/IDomainRepository';
import { DDD_COLLECTIONS, DEFAULT_DDD_PROJECT_ID } from '../../../../config/collections';

export class MongoDomainRepository implements IDomainRepository {
  private collection?: Collection;

  constructor(
    private readonly database: Db
  ) {
    // DO NOT initialize collection in constructor!
    // MongoDB might not be connected yet during initialization
  }

  private async getCollection(): Promise<Collection> {
    // Lazy initialization - only get collection when actually needed
    if (!this.collection) {
      this.collection = this.database.collection(DDD_COLLECTIONS.DOMAINS);
    }
    return this.collection;
  }

  async save(domain: Domain): Promise<void> {
    const collection = await this.getCollection();
    const doc = domain.toJSON();
    await collection.replaceOne(
      { domainId: domain.id },
      doc,
      { upsert: true }
    );
  }

  async findById(id: string): Promise<Domain | null> {
    const collection = await this.getCollection();
    const doc = await collection.findOne({ domainId: id });
    if (!doc) return null;
    
    return Domain.reconstitute(
      {
        name: doc.name,
        description: doc.description,
        vision: doc.vision,
        ubiquitousLanguage: doc.ubiquitousLanguage || {}
      },
      doc.domainId,
      new Date(doc.createdAt),
      new Date(doc.updatedAt)
    );
  }

  async findByName(name: string): Promise<Domain | null> {
    const collection = await this.getCollection();
    const doc = await collection.findOne({ name });
    if (!doc) return null;
    
    return Domain.reconstitute(
      {
        name: doc.name,
        description: doc.description,
        vision: doc.vision,
        ubiquitousLanguage: doc.ubiquitousLanguage || {}
      },
      doc.domainId,
      new Date(doc.createdAt),
      new Date(doc.updatedAt)
    );
  }

  async findAll(options?: {
    page?: number;
    pageSize?: number;
    searchTerm?: string;
    includeArchived?: boolean;
  }): Promise<{ domains: Domain[]; totalCount: number }> {
    const collection = await this.getCollection();
    const page = options?.page || 1;
    const pageSize = options?.pageSize || 100;
    const skip = (page - 1) * pageSize;

    const query: any = {};
    const conditions: any[] = [];
    
    // Add projectId filter - include default project or null/missing values
    conditions.push({
      $or: [
        { projectId: DEFAULT_DDD_PROJECT_ID },
        { projectId: { $exists: false } },
        { projectId: null }
      ]
    });
    
    if (options?.searchTerm) {
      conditions.push({
        $or: [
          { name: { $regex: options.searchTerm, $options: 'i' } },
          { description: { $regex: options.searchTerm, $options: 'i' } }
        ]
      });
    }
    
    if (conditions.length > 0) {
      query.$and = conditions;
    }

    const [docs, totalCount] = await Promise.all([
      collection
        .find(query)
        .skip(skip)
        .limit(pageSize)
        .toArray(),
      collection.countDocuments(query)
    ]);

    const domains = docs.map(doc => 
      Domain.reconstitute(
        {
          name: doc.name,
          description: doc.description,
          vision: doc.vision,
          ubiquitousLanguage: doc.ubiquitousLanguage || {}
        },
        doc.domainId,
        new Date(doc.createdAt),
        new Date(doc.updatedAt)
      )
    );

    return { domains, totalCount };
  }

  async deleteById(id: string): Promise<void> {
    const collection = await this.getCollection();
    await collection.deleteOne({ domainId: id });
  }

  async existsByName(name: string): Promise<boolean> {
    const collection = await this.getCollection();
    const count = await collection.countDocuments({ name });
    return count > 0;
  }
}